import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  Ban,
  CalendarDays,
  ChefHat,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileClock,
  Flame,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  Utensils,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Tag } from '@/components/ui/tag';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import {
  approveAdminCustomRecipe,
  deleteAdminCustomRecipe,
  getAdminCustomRecipeAuditLog,
  getAdminCustomRecipeDetails,
  getAdminCustomRecipes,
  type AdminCustomRecipeAuditLogItem,
  type GetAdminCustomRecipesParams,
} from '@/lib/api/mealPlans';
import type { CustomRecipe, NormalizedRecipeDetailsData } from '@/types/custom-recipe';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { COOKING_METHOD_OPTIONS } from '@/constants/dietary-goals';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import RecipeIngredientCards from '@/components/recipes/RecipeIngredientCards';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

type ApprovalStatus = NonNullable<GetAdminCustomRecipesParams['approvalStatus']>;
type SharingStatus = NonNullable<GetAdminCustomRecipesParams['sharingStatus']>;

const formatDate = (value?: string): string => {
  if (!value) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
};

const formatNutrientQuantity = (value: number | undefined, unit: string): string => {
  const next = Number(value ?? 0);
  if (!Number.isFinite(next)) return `0 ${unit}`;
  const rounded = Math.round(next * 100) / 100;
  const label = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/\.?0+$/, '');
  return `${label} ${unit}`;
};

const formatMacro = (value?: number): string => {
  return formatNutrientQuantity(value, 'g');
};

const formatCalories = (value?: number): string => {
  const next = Number(value ?? 0);
  return Number.isFinite(next) ? `${Math.round(next)} kcal` : '0 kcal';
};

const formatTime = (minutes?: number): string => {
  const next = Number(minutes ?? 0);
  if (!Number.isFinite(next) || next <= 0) return '0m';
  if (next < 60) return `${Math.round(next)}m`;
  const hours = Math.floor(next / 60);
  const mins = Math.round(next % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatServingNumber = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const formatServingUnitName = (value: string, quantity: number): string => {
  const normalized = formatUnderscoreDisplayLabel(value).toLowerCase();
  if (!normalized) return '';
  if (quantity === 1 || normalized.endsWith('s')) return normalized;
  return `${normalized}s`;
};

const formatServingSizeLabel = (serving?: {
  label?: string | null;
  servingId?: string;
  grams?: number | null;
  quantity?: number | null;
}): string => {
  if (!serving) return 'Serving';
  const label = formatUnderscoreDisplayLabel(serving.label || serving.servingId || 'Serving');
  const servingId = formatUnderscoreDisplayLabel(serving.servingId || '');
  const grams = Number(serving.grams);
  const quantity = Number(serving.quantity);
  const hasGrams = Number.isFinite(grams) && grams > 0;
  const hasQuantity = Number.isFinite(quantity) && quantity > 0;

  if (hasQuantity) {
    const unitName = formatServingUnitName(servingId && servingId !== label ? servingId : label, quantity);
    const quantityText = `${formatServingNumber(quantity)}${unitName ? ` ${unitName}` : ''}`;
    return hasGrams
      ? `${formatServingNumber(grams)} gram (${quantityText})`
      : `${label} (${quantityText})`;
  }

  return hasGrams ? `${label} (${formatServingNumber(grams)}g)` : label;
};

const formatAuditAction = (action: string): string => (
  action.replace(/_/g, ' ').replace(/^\w/, (letter) => letter.toUpperCase())
);

const getCreatorName = (recipe: CustomRecipe): string => {
  if (recipe.createdBy && typeof recipe.createdBy === 'object') {
    return recipe.createdBy.name || recipe.createdBy.id || 'Unknown';
  }
  return typeof recipe.createdBy === 'string' ? recipe.createdBy : 'Unknown';
};

const ApprovalBadge: React.FC<{ recipe: Pick<CustomRecipe, 'isVerified' | 'isPublic'> }> = ({ recipe }) => {
  if (recipe.isVerified) {
    return <Tag variant="success" size="sm" className="border-[#BDE9D6] bg-[#EEFBF5] text-[#167A55]">Approved</Tag>;
  }

  return <Tag variant="warning" size="sm" className="border-[#F2D59A] bg-[#FFF8E7] text-[#9A6400]">Pending</Tag>;
};

const SharingBadge: React.FC<{ isPublic?: boolean }> = ({ isPublic }) => (
  <Tag variant="outlined" size="sm" className="border-[#CDE3E9] bg-[#F7FBFC] text-[#334650]">
    {isPublic ? 'Public' : 'Private'}
  </Tag>
);

const SummaryMetric: React.FC<{
  label: string;
  value: number | string;
  detail: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  bar: string;
}> = ({ label, value, detail, icon: Icon, accent, bar }) => (
  <div className="relative overflow-hidden rounded-2xl border border-[#dbe8ed] bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
    <span className={`absolute inset-x-0 top-0 h-1 ${bar}`} />
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6b7c85]">{label}</p>
        <p className="mt-3 text-4xl font-bold leading-none tracking-tight text-[#12303a]">{value}</p>
      </div>
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
    <p className="mt-3 min-h-5 text-sm text-[#5d6f78]">{detail}</p>
  </div>
);

const AdminDetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-1.5 rounded-md bg-[#f5fafc] p-3">
    <p className="text-xs font-semibold uppercase tracking-[0.04em] text-[#6b7c85]">{label}</p>
    <div className="break-words text-sm font-medium text-[#17262f]">{value}</div>
  </div>
);

const TagGroup: React.FC<{
  label: string;
  values: string[];
  type?: 'dietLabels' | 'allergies' | 'healthLabels' | 'cuisineTypes';
  tone?: 'default' | 'danger' | 'warning';
}> = ({ label, values, type, tone = 'default' }) => {
  const variant = tone === 'danger' ? 'danger' : tone === 'warning' ? 'warning' : 'outlined';

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.04em] text-[#6b7c85]">{label}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {values.length > 0 ? values.map((value) => (
          <Tag
            key={`${label}-${value}`}
            variant={variant}
            size="sm"
            maxWidth="100%"
            startIcon={tone === 'danger' ? <Ban className="h-3 w-3" /> : tone === 'warning' ? <AlertTriangle className="h-3 w-3" /> : undefined}
            className={tone === 'default' ? 'border-[#CDE3E9] bg-white text-[#334650]' : undefined}
          >
            {type ? getDietaryDisplayName(value, type) : value.replace(/_/g, ' ')}
          </Tag>
        )) : <span className="text-sm text-[#8a9aa2]">-</span>}
      </div>
    </div>
  );
};

const RecipeDetailsDialog: React.FC<{
  recipe: NormalizedRecipeDetailsData | null;
  loading: boolean;
  approving: boolean;
  deleting: boolean;
  auditLogs: AdminCustomRecipeAuditLogItem[];
  auditLoading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: () => void;
  onDelete: () => void;
}> = ({ recipe, loading, approving, deleting, auditLogs, auditLoading, open, onOpenChange, onApprove, onDelete }) => {
  const ingredientsData = recipe && Array.isArray(recipe.ingredients) && recipe.ingredients.length > 0
    ? recipe.ingredients.map((ingredient) => {
        const quantity = Number(ingredient.quantity);
        const unit = String(ingredient.unit ?? '').trim();
        const name = String(ingredient.name ?? '').trim();
        const qtyText = Number.isFinite(quantity) && quantity > 0 ? formatServingNumber(quantity) : '';
        return { quantity: `${qtyText}${qtyText && unit ? ' ' : ''}${unit}`.trim(), name };
      })
    : (recipe?.ingredientLines ?? []).map((line) => {
        const parts = line.split(' ');
        const quantity = parts.slice(0, 2).join(' ');
        const name = parts.slice(2).join(' ') || parts.slice(1).join(' ');
        return { quantity, name };
      });

  const cookingMethodLabel = recipe?.cookingMethod
    ? (COOKING_METHOD_OPTIONS.find((option) => option.value === recipe.cookingMethod)?.name
      ?? recipe.cookingMethod.replace(/_/g, ' '))
    : 'Not specified';
  const defaultServing = recipe?.servingSizes?.find((serving) => serving.isDefault) || recipe?.servingSizes?.[0];
  const displayImage = recipe?.recipeImageUrl?.trim() || DEFAULT_RECIPE_IMAGE;
  const combinedTags = recipe
    ? [...recipe.mealTypes, ...recipe.dishTypes, ...(recipe.tags ?? [])].filter(Boolean)
    : [];

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[88vh] max-w-6xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{recipe?.recipeName || 'Custom recipe'}</DialogTitle>
        <DialogDescription>
          {recipe ? `${recipe.servings} serving${recipe.servings === 1 ? '' : 's'} - ${recipe.totalTimeMinutes || 0} min` : 'Loading recipe details'}
        </DialogDescription>
      </DialogHeader>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-[#5d6f78]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading recipe
        </div>
      ) : recipe ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1.05fr)_minmax(20rem,0.95fr)]">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <SharingBadge isPublic={recipe.isPublic} />
                <ApprovalBadge recipe={recipe} />
              </div>
              <img
                src={displayImage}
                alt={recipe.recipeName}
                className="h-64 w-full rounded-lg object-cover"
              />
              <div className="rounded-lg border border-[#d7e6eb] p-4">
                <h3 className="text-sm font-semibold text-[#17262f]">Description</h3>
                <p className="mt-2 text-sm leading-6 text-[#334650]">
                  {recipe.description?.trim() || 'No description available.'}
                </p>
              </div>
              <div className="rounded-lg border border-[#d7e6eb] p-4">
                <h3 className="text-sm font-semibold text-[#17262f]">Quick info</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <AdminDetailField label="Time" value={formatTime(recipe.totalTimeMinutes)} />
                  <AdminDetailField label="Prep / Cook" value={`${formatTime(recipe.prepTimeMinutes)} / ${formatTime(recipe.cookTimeMinutes)}`} />
                  <AdminDetailField label="Cooking method" value={cookingMethodLabel} />
                  <AdminDetailField label="Servings" value={recipe.servings} />
                  <AdminDetailField label="Nutrition servings" value={recipe.nutritionServings || '-'} />
                  <AdminDetailField label="Ingredients" value={ingredientsData.length} />
                  <AdminDetailField label="Default serving" value={formatServingSizeLabel(defaultServing)} />
                  <AdminDetailField label="Updated" value={formatDate(recipe.updatedAt)} />
                </div>
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.04em] text-[#6b7c85]">Available serving sizes</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {recipe.servingSizes.length > 0 ? recipe.servingSizes.map((serving) => (
                      <Badge
                        key={serving.servingId}
                        variant={serving.isDefault ? 'default' : 'outline'}
                        className={serving.isDefault ? 'bg-[#2EB4E4] text-white hover:bg-[#20AEDC]' : 'border-[#cde3e9] bg-white text-[#334650]'}
                      >
                        {formatServingSizeLabel(serving)}
                      </Badge>
                    )) : <span className="text-sm text-[#8a9aa2]">No serving sizes added.</span>}
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#17262f]">Ingredients</h3>
                <div className="mt-3">
                  <RecipeIngredientCards items={ingredientsData} />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border border-[#d7e6eb] p-4">
                <h3 className="text-sm font-semibold text-[#17262f]">Nutrition per serving</h3>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-md bg-[#f5fafc] p-3">
                    <p className="text-xs text-[#6b7c85]">Calories</p>
                    <p className="font-semibold text-[#17262f]">{formatCalories(recipe.caloriesPerServing)}</p>
                  </div>
                  <div className="rounded-md bg-[#f5fafc] p-3">
                    <p className="text-xs text-[#6b7c85]">Protein</p>
                    <p className="font-semibold text-[#17262f]">{formatMacro(recipe.proteinPerServing)}</p>
                  </div>
                  <div className="rounded-md bg-[#f5fafc] p-3">
                    <p className="text-xs text-[#6b7c85]">Carbs</p>
                    <p className="font-semibold text-[#17262f]">{formatMacro(recipe.carbsPerServing)}</p>
                  </div>
                  <div className="rounded-md bg-[#f5fafc] p-3">
                    <p className="text-xs text-[#6b7c85]">Fat</p>
                    <p className="font-semibold text-[#17262f]">{formatMacro(recipe.fatPerServing)}</p>
                  </div>
                  <div className="rounded-md bg-[#f5fafc] p-3">
                    <p className="text-xs text-[#6b7c85]">Fiber</p>
                    <p className="font-semibold text-[#17262f]">{formatMacro(recipe.fiberPerServing)}</p>
                  </div>
                  <div className="rounded-md bg-[#f5fafc] p-3">
                    <p className="text-xs text-[#6b7c85]">Sugar</p>
                    <p className="font-semibold text-[#17262f]">{formatMacro(recipe.sugarPerServing)}</p>
                  </div>
                  <div className="rounded-md bg-[#f5fafc] p-3">
                    <p className="text-xs text-[#6b7c85]">Saturated fat</p>
                    <p className="font-semibold text-[#17262f]">{formatMacro(recipe.saturatedFatPerServing)}</p>
                  </div>
                  <div className="rounded-md bg-[#f5fafc] p-3">
                    <p className="text-xs text-[#6b7c85]">Sodium</p>
                    <p className="font-semibold text-[#17262f]">{formatNutrientQuantity(recipe.sodiumPerServing, 'mg')}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-[#d7e6eb] p-4">
                <h3 className="text-sm font-semibold text-[#17262f]">Tags</h3>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <TagGroup label="Diet labels" values={recipe.dietLabels} type="dietLabels" />
                  <TagGroup label="Allergies" values={recipe.allergens} type="allergies" tone="danger" />
                  <TagGroup label="Intolerance" values={recipe.intoleranceTags} type="healthLabels" tone="warning" />
                  <TagGroup label="Cuisine types" values={recipe.cuisines} type="cuisineTypes" />
                  <TagGroup label="Meal / dish / other" values={combinedTags} />
                </div>
              </div>

              <div className="rounded-lg border border-[#d7e6eb] p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-[#17262f]">
                  <ChefHat className="h-4 w-4" />
                  Instructions
                </h3>
                {recipe.cookingInstructions.length > 0 ? (
                  <ol className="mt-3 space-y-2 text-sm leading-6 text-[#334650]">
                    {recipe.cookingInstructions.map((instruction, index) => (
                      <li key={`${index}-${instruction}`} className="flex gap-3">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#eef8fb] text-xs font-semibold text-[#167f9d]">
                          {index + 1}
                        </span>
                        <span>{instruction.replace(/^- /, '')}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="mt-3 text-sm text-[#5d6f78]">No cooking instructions added.</p>
                )}
              </div>

              <div className="rounded-lg border border-[#d7e6eb] p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-[#17262f]">
                  <ChefHat className="h-4 w-4" />
                  Cooking tips
                </h3>
                {recipe.cookingTips?.trim().length ? (
                  <ul className="mt-3 space-y-2 text-sm leading-6 text-[#334650]">
                    {recipe.cookingTips.split('\n').filter((tip) => tip.trim().length > 0).map((tip, index) => (
                      <li key={`${index}-${tip}`} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#8a9aa2]" />
                        <span>{tip.replace(/^- /, '')}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-[#5d6f78]">No cooking tips added.</p>
                )}
              </div>

              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-[#17262f]">
                  <FileClock className="h-4 w-4" />
                  Audit log
                </h3>
                <div className="mt-3 space-y-2 text-sm">
                  {auditLoading ? (
                    <div className="flex items-center gap-2 rounded-md border border-[#e4eef2] px-3 py-2 text-[#5d6f78]">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading audit log
                    </div>
                  ) : auditLogs.length > 0 ? (
                    auditLogs.map((log) => (
                      <div key={log.id || `${log.action}-${log.createdAt}`} className="rounded-md border border-[#e4eef2] px-3 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-[#17262f]">{formatAuditAction(log.action)}</span>
                          <span className="text-xs text-[#6b7c85]">{formatDate(log.createdAt)}</span>
                        </div>
                        <p className="mt-1 text-xs text-[#5d6f78]">
                          {log.actor?.email || log.actor?.id || 'Unknown actor'}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-md border border-[#e4eef2] px-3 py-2 text-[#5d6f78]">No audit events yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-12 text-center text-sm text-[#5d6f78]">Recipe details unavailable.</div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
        <Button type="button" onClick={onApprove} disabled={!recipe || recipe.isVerified || approving}>
          {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Approve
        </Button>
        <Button type="button" variant="destructive" onClick={onDelete} disabled={!recipe || deleting}>
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

const CustomRecipeReviewPage: React.FC = () => {
  const [recipes, setRecipes] = useState<CustomRecipe[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>('pending');
  const [sharingStatus, setSharingStatus] = useState<SharingStatus>('all');
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<NormalizedRecipeDetailsData | null>(null);
  const [auditLogs, setAuditLogs] = useState<AdminCustomRecipeAuditLogItem[]>([]);
  const [isLoadingAuditLog, setIsLoadingAuditLog] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [approvingRecipeId, setApprovingRecipeId] = useState<string | null>(null);
  const [deletingRecipeId, setDeletingRecipeId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Pick<CustomRecipe, 'id' | 'title'> | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageStartIndex = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEndIndex = Math.min(page * pageSize, totalCount);

  const loadRecipes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAdminCustomRecipes({
        page,
        pageSize,
        q: search,
        approvalStatus,
        sharingStatus,
      });
      setRecipes(response.data);
      setTotalCount(response.pagination.totalCount);
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to load custom recipes right now.';
      toast({
        title: 'Recipes failed to load',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [approvalStatus, page, pageSize, search, sharingStatus]);

  useEffect(() => {
    void loadRecipes();
  }, [loadRecipes]);

  const loadRecipeDetails = useCallback(async (recipeId: string) => {
    setSelectedRecipeId(recipeId);
    setSelectedRecipe(null);
    setAuditLogs([]);
    setIsLoadingDetails(true);
    setIsLoadingAuditLog(true);
    try {
      const [response, auditResponse] = await Promise.all([
        getAdminCustomRecipeDetails(recipeId),
        getAdminCustomRecipeAuditLog(recipeId),
      ]);
      setSelectedRecipe(response.data);
      setAuditLogs(auditResponse.data);
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to load recipe details.';
      toast({
        title: 'Recipe details failed to load',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingDetails(false);
      setIsLoadingAuditLog(false);
    }
  }, []);

  const handleApprove = async (recipeId: string) => {
    setApprovingRecipeId(recipeId);
    try {
      await approveAdminCustomRecipe(recipeId);
      setRecipes((current) =>
        current.map((recipe) =>
          recipe.id === recipeId ? { ...recipe, isVerified: true } : recipe
        )
      );
      setSelectedRecipe((current) =>
        current && current.id === recipeId ? { ...current, isVerified: true } : current
      );
      toast({
        title: 'Recipe approved',
        description: 'Public sharing is now available for this recipe.',
      });
      void loadRecipes();
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to approve this recipe.';
      toast({
        title: 'Approval failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setApprovingRecipeId(null);
    }
  };

  const handleDelete = async (recipeId: string) => {
    setDeletingRecipeId(recipeId);
    try {
      await deleteAdminCustomRecipe(recipeId);
      setRecipes((current) => current.filter((recipe) => recipe.id !== recipeId));
      setTotalCount((current) => Math.max(0, current - 1));
      if (selectedRecipeId === recipeId) {
        setSelectedRecipeId(null);
        setSelectedRecipe(null);
        setAuditLogs([]);
      }
      setDeleteTarget(null);
      toast({
        title: 'Recipe deleted',
        description: 'The custom recipe was removed from active review and search results.',
      });
      void loadRecipes();
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to delete this recipe.';
      toast({
        title: 'Delete failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setDeletingRecipeId(null);
    }
  };

  const pendingCount = useMemo(
    () => recipes.filter((recipe) => !recipe.isVerified).length,
    [recipes]
  );
  const approvedCount = useMemo(
    () => recipes.filter((recipe) => recipe.isVerified).length,
    [recipes]
  );

  return (
    <main className="min-h-[calc(100vh-7.5rem)] overflow-hidden rounded-[28px] border border-[#dbe6eb] bg-white px-4 py-5 sm:px-6 lg:px-8">
      <div className="w-full space-y-5">
        <section className="flex flex-col gap-3 border-b border-[#dbe8ed] pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <Tag variant="outlined" size="sm" className="w-fit border-[#BFE4ED] bg-white text-[#167F9D]">
              Admin review
            </Tag>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-[#12303a] sm:text-3xl">Custom recipe review</h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-[#4b626c]">
              Review custom recipes created by practitioners before they become available for public sharing.
            </p>
          </div>
        </section>

        <div className="grid gap-4 sm:grid-cols-3">
          <SummaryMetric
            label="Total recipes"
            value={totalCount}
            detail="Matching the current filters"
            icon={ChefHat}
            accent="bg-[#eaf6fa] text-[#167F9D]"
            bar="bg-gradient-to-r from-[#2EB4E4] to-[#167F9D]"
          />
          <SummaryMetric
            label="Pending review"
            value={pendingCount}
            detail="Awaiting approval on this page"
            icon={FileClock}
            accent="bg-[#FFF8E7] text-[#9A6400]"
            bar="bg-gradient-to-r from-[#F5C451] to-[#D99511]"
          />
          <SummaryMetric
            label="Approved"
            value={approvedCount}
            detail="Verified for public sharing on this page"
            icon={ShieldCheck}
            accent="bg-[#EEFBF5] text-[#167A55]"
            bar="bg-gradient-to-r from-[#4FC58C] to-[#167A55]"
          />
        </div>

        <section className="overflow-hidden rounded-2xl border border-[#dbe8ed] bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-[#e4eef2] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6b7c85]">Review queue</p>
              <h2 className="mt-1 text-lg font-semibold text-[#17262f]">All recipes</h2>
            </div>
            <span className="w-fit rounded-full border border-[#cde3e9] bg-[#f5fafc] px-3 py-1 text-xs font-semibold text-[#167F9D]">
              {totalCount} total
            </span>
          </div>
          <div className="px-4 py-4 sm:px-6">
            <div className="grid gap-3 border-b border-[#eef4f6] pb-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
              <div className="grid gap-3 md:grid-cols-[minmax(16rem,1fr)_12rem_12rem]">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7c85]" />
                  <Input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder="Search recipes"
                    className="bg-white pl-9"
                  />
                </div>
                <Select
                  value={approvalStatus}
                  onValueChange={(value) => {
                    setApprovalStatus(value as ApprovalStatus);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="border-[#cde3e9] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="all">All approvals</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={sharingStatus}
                  onValueChange={(value) => {
                    setSharingStatus(value as SharingStatus);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="border-[#cde3e9] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sharing</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-[minmax(5rem,6rem)_1fr] gap-2 sm:flex sm:items-center sm:justify-end">
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-10 w-full border-[#cde3e9] bg-white sm:w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={String(option)}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" className="justify-center bg-white" onClick={() => void loadRecipes()} disabled={isLoading}>
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Refresh
                </Button>
              </div>
            </div>

          {isLoading && recipes.length === 0 ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="overflow-hidden rounded-lg border border-[#dbe8ed] bg-white shadow-sm">
                  <div className="h-44 w-full animate-pulse bg-[#e8f1f4]" />
                  <div className="space-y-3 p-4">
                    <div className="h-4 w-2/3 animate-pulse rounded bg-[#e8f1f4]" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-[#eef4f6]" />
                    <div className="h-9 w-full animate-pulse rounded bg-[#eef4f6]" />
                  </div>
                </div>
              ))}
            </div>
          ) : recipes.length === 0 ? (
            <div className="mt-4 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[#cfdfe6] bg-[#f9fcfd] px-4 py-20 text-center text-sm text-[#5d6f78]">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#eef5f8] text-[#94a8b0]">
                <ChefHat className="h-7 w-7" />
              </span>
              <p className="font-medium text-[#334650]">No custom recipes found</p>
              <p className="text-xs text-[#8a9aa2]">Try adjusting your search or filters.</p>
            </div>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {recipes.map((recipe) => {
                const isApproving = approvingRecipeId === recipe.id;
                const isDeleting = deletingRecipeId === recipe.id;
                return (
                  <article
                    key={recipe.id}
                    className="group flex flex-col overflow-hidden rounded-lg border border-[#d7e6eb] bg-white shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="relative h-44 w-full overflow-hidden bg-[#eef5f8]">
                      <img
                        src={recipe.image?.trim() || DEFAULT_RECIPE_IMAGE}
                        alt={recipe.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(event) => {
                          const target = event.currentTarget;
                          if (target.src !== DEFAULT_RECIPE_IMAGE) target.src = DEFAULT_RECIPE_IMAGE;
                        }}
                      />
                      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/25 to-transparent" />
                      <div className="absolute left-3 top-3 flex flex-wrap gap-2">
                        <ApprovalBadge recipe={recipe} />
                        <SharingBadge isPublic={recipe.isPublic} />
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="truncate text-base font-semibold text-[#17262f]" title={recipe.title}>
                        {recipe.title}
                      </h3>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#6b7c85]">
                        <span className="inline-flex items-center gap-1">
                          <Flame className="h-3.5 w-3.5 text-[#c76b2a]" />
                          {formatCalories(recipe.totalCalories)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Utensils className="h-3.5 w-3.5 text-[#5d6f78]" />
                          {recipe.ingredientCount || 0} ingredients
                        </span>
                      </div>

                      <div className="mt-3 space-y-1.5 border-t border-[#eef4f6] pt-3 text-xs text-[#5d6f78]">
                        <p className="flex items-center gap-1.5">
                          <UserRound className="h-3.5 w-3.5 shrink-0 text-[#94a8b0]" />
                          <span className="truncate">{getCreatorName(recipe)}</span>
                        </p>
                        <p className="flex items-center gap-1.5">
                          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-[#94a8b0]" />
                          Updated {formatDate(recipe.updatedAt)}
                        </p>
                      </div>

                      <div className="mt-4 grid grid-cols-[1fr_1fr_2.75rem] items-center gap-2 border-t border-[#eef4f6] pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => void loadRecipeDetails(recipe.id)}
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="w-full"
                          disabled={recipe.isVerified || isApproving}
                          onClick={() => void handleApprove(recipe.id)}
                        >
                          {isApproving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                          Approve
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="w-full"
                          aria-label="Delete recipe"
                          disabled={isDeleting}
                          onClick={() => setDeleteTarget({ id: recipe.id, title: recipe.title })}
                        >
                          {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

            {totalCount > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-[#eef4f6] pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-[#5d6f78]">
                  Showing <span className="font-semibold text-[#17262f]">{pageStartIndex}-{pageEndIndex}</span> of {totalCount}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || isLoading}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-[#5d6f78]">Page {page} of {totalPages}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || isLoading}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <RecipeDetailsDialog
        open={Boolean(selectedRecipeId)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedRecipeId(null);
            setSelectedRecipe(null);
            setAuditLogs([]);
          }
        }}
        recipe={selectedRecipe}
        loading={isLoadingDetails}
        auditLogs={auditLogs}
        auditLoading={isLoadingAuditLog}
        approving={Boolean(selectedRecipeId && approvingRecipeId === selectedRecipeId)}
        deleting={Boolean(selectedRecipeId && deletingRecipeId === selectedRecipeId)}
        onApprove={() => {
          if (selectedRecipeId) {
            void handleApprove(selectedRecipeId);
          }
        }}
        onDelete={() => {
          if (selectedRecipeId) {
            setDeleteTarget({ id: selectedRecipeId, title: selectedRecipe?.recipeName || 'this recipe' });
          }
        }}
      />
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete custom recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {deleteTarget?.title || 'this recipe'} from active recipe lists. The audit log will retain who deleted it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingRecipeId)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={Boolean(deletingRecipeId)}
              onClick={(event) => {
                event.preventDefault();
                if (deleteTarget) {
                  void handleDelete(deleteTarget.id);
                }
              }}
            >
              {deletingRecipeId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};

export default CustomRecipeReviewPage;
